/**
 * Shared access rules for MeGen features.
 *
 * The admin tab and the contextual AI Usage button must use the same
 * permission source. Keep this helper pure so it can be reused and tested
 * without mounting the HIS shell.
 */

export const MEGEN_COST_MENU_KEY = "MeGenCost" as const;

export type AdminMenuAccessContext = {
  adminMenuDivisions?: Record<string, unknown>;
  selectedDivisionId?: number | string | null;
  loginWithSocial?: boolean | string | null;
  isSuperUser?: boolean | null;
  divisionType?: string | null;
};

const hasMenu = (menuMap: unknown, menuKey: string): boolean =>
  !!menuMap && typeof menuMap === "object" && Object.prototype.hasOwnProperty.call(menuMap, menuKey);

/**
 * Mirrors the admin-tab access rule in MainHIS:
 * - superusers and backoffice users can access the menu;
 * - social login is scoped to the selected division;
 * - regular login uses the global `all` menu map.
 */
export const canAccessAdminMenu = (
  context: AdminMenuAccessContext,
  menuKey: string
): boolean => {
  if (context.isSuperUser || context.divisionType === "backoffice") return true;

  const menuMap = context.loginWithSocial
    ? context.adminMenuDivisions?.[String(context.selectedDivisionId ?? "")]
    : context.adminMenuDivisions?.all;

  return hasMenu(menuMap, menuKey);
};

export const canAccessMeGenCost = (context: AdminMenuAccessContext): boolean =>
  canAccessAdminMenu(context, MEGEN_COST_MENU_KEY);
